<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useCorporativeAccount } from '@/store/corporative'
import useAuthStore from '@/store/auth'
import { useLoading } from '@/composables/useLoading'
import { globalPermissionsGuard } from '@/utils/globalPermissionsGuard'

import { pickSupportedLocale } from '../src/locales/i18n-schema' // 'pt-BR' | 'en' | 'es'
import { useKcAuthSnapshot } from '@/composables/useKcAuthSnapshot'
import AlertDialog from '@/components/AlertDialog.vue'
import alertIcon from '@/assets/icons/white/attention.svg'

/* -----------------------------
 * Stores & Guards
 * ----------------------------- */
const corporativeAccountStore = useCorporativeAccount()
const { getCorporates } = corporativeAccountStore

const authStore = useAuthStore()
const { getUserInfo } = authStore
const { user } = storeToRefs(authStore)

const { isAllowed } = globalPermissionsGuard()
const { isAuthenticated } = useKcAuthSnapshot()

const { locale } = useI18n({ useScope: 'global' })

const rawLocale = computed<unknown>(() => {
  return user.value?.locale ?? localStorage.getItem('lang') ?? 'pt-BR'
  // deixa como portugues como padrão a principio
  // pode ser alterado para retornar a linguagem do navegador padrão com navigator.language (traduções necessárias)
})

function setLocaleSafe(val: unknown): void {
  const normalized = pickSupportedLocale(val ?? 'pt-BR')
  if (locale.value !== normalized) {
    locale.value = normalized
  }
}

watch(rawLocale, (val) => setLocaleSafe(val), { immediate: true })

const isOpen = ref(false)

function handleClose(): void {
  isOpen.value = false
  isAllowed.value = true
}

watch(isAllowed, (ok) => {
  isOpen.value = ok === false
})

onMounted(async () => {
  if (isAuthenticated.value === false) return
  else {
    await getUserInfo()

    useLoading(async () => {
      await getCorporates()
    })
  }
})
</script>

<template>
  <div>
    <router-view />

    <AlertDialog
      :is-open="isOpen"
      color="alert"
      class="flex !min-w-full flex-col !px-10 text-sm"
      :text="$t('permissions.blockedAccessDialog')"
      :icon="alertIcon"
      @close="handleClose"
    >
      <div class="my-4 flex gap-5">
        <Button class="min-w-36 bg-alert text-base" @click="handleClose">
          {{ $t('general.ok') }}
        </Button>
      </div>
    </AlertDialog>
  </div>
</template>

<style>
html {
  overflow-x: hidden !important;
  overflow-y: auto !important;
}

body,
#app {
  width: 100vw;
  height: 100vh;
  overflow: auto;
}

.dash:before {
  content: '|';
  color: #8dc63f;
  margin-right: 0.5rem;
}

/* tamanho base dos scrolls */
::-webkit-scrollbar {
  width: 0.44rem;
  height: 0.44rem;
}

::-webkit-scrollbar-thumb {
  background-color: rgba(85, 85, 85, 0.35);
  border-radius: 0.19rem;
}

/* VERTICAL: com borda (risquinho) */
::-webkit-scrollbar-track:vertical {
  background: transparent;
  border-right: 0.06rem solid #c5c5c5;
}

/* HORIZONTAL: sem borda */
::-webkit-scrollbar-track:horizontal {
  background: transparent;
  border-right: none;
}

@-moz-document url-prefix() {
  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(85, 85, 85, 0.35) transparent;
  }
  ::-moz-scrollbar-thumb {
    background-color: rgba(85, 85, 85, 0.35);
    border-radius: 0.19rem;
  }
  ::-moz-scrollbar-track {
    background: transparent;
    border-right: 0.06rem solid #c5c5c5;
  }

  .scroll {
    padding-right: 0.94rem !important;
  }
}

::-webkit-scrollbar:horizontal {
  height: 0;
}

.v-label {
  font-size: 0.875rem !important;
  font-weight: 400 !important;
  line-height: 1.425 !important;
  color: #212121 !important;
}

table {
  border: 0.06rem solid #d8d8d8;
  border-radius: 4px;
  word-break: break-all;
  border-collapse: inherit !important;
}

.v-table > .v-table__wrapper > table > thead > tr > th {
  overflow-wrap: break-word !important;
  word-break: normal !important;
  white-space: nowrap !important;
}

@media (max-width: 1280px) {
  .v-table > .v-table__wrapper > table > thead > tr > th {
    overflow-wrap: break-word !important;
    word-break: normal !important;
    white-space: unset !important;
  }
}

.v-table > .v-table__wrapper > table {
  word-break: keep-all !important;
}

.v-pagination__item--is-active {
  background-color: #8dc63f !important;
}

.v-pagination .v-btn:hover {
  background-color: #8dc63f !important;
}

.v-btn:hover > .v-btn__overlay {
  opacity: 0 !important;
}

.v-switch--inset .v-switch__thumb {
  height: 18px !important;
  width: 18px !important;
  transform: none !important;
}

.v-switch--inset .v-switch__track {
  height: 22px !important;
  min-width: 36px !important;
  opacity: 1 !important;
}

.v-switch__track {
  background-color: #e0e0e0 !important;
  max-height: 14px !important;
  max-width: 36px !important;
}

.v-switch__thumb {
  height: 20px !important;
  width: 20px !important;
  background-color: #9e9e9e !important;
}

input[type='time']::-webkit-calendar-picker-indicator,
input[type='date']::-webkit-calendar-picker-indicator {
  display: none !important;
}

.v-expansion-panel-text__wrapper {
  padding: 0 0 0 0 !important;
}

* {
  box-sizing: border-box;
}
</style>
